import React, { Component } from "react"
import { Arwes, Button, Loading, Words } from "arwes"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { commentPost, deletePost, fetchSinglePost } from "./redux/reducers"
import { formatPostDate } from "../../common/util/dates"
import { mdiChemicalWeapon, mdiRobot } from "@mdi/js"
import Icon from "@mdi/react"
import styled from "styled-components"
import { Field, getFormValues, reduxForm, reset } from "redux-form"
import get from "lodash.get"
import Comment from "./Comment"
import { isAdminUser } from "../../common/util/isAdmin"
import UserAvatar from "../../common/UserAvatar"
import { MarginTop } from "../../common/styled/MarginTop"
import {
  ContentColumn,
  PageBlock as SharedPageBlock,
  PageOffset
} from "../../common/styled/Page"

// Post Detail v2 — "one thread, one grid" (postArea/cp-postdetail.jsx).
// Every panel (post, replies, composer) shares the same two-column grid:
// a fixed 96px author rail + one content edge. Avatars of all sizes sit
// centered on the same vertical spine (x = 48).
const RAIL = 96
const SPINE_X = RAIL / 2

const PageBlock = styled(SharedPageBlock)`
  margin: 16px 30px;

  @media (max-width: 900px) {
    margin: 12px;
  }

  @media (max-width: 360px) {
    margin-left: 8px;
    margin-right: 8px;
  }
`

/* ---------- cp-frame: bracket frame from the design tokens ---------- */

const TONES = {
  primary: { border: "rgba(190,38,252,0.7)", bg: "rgba(8,4,40,0.85)", corner: "#be26fc" },
  magenta: { border: "rgba(252,38,250,0.6)", bg: "rgba(30,0,28,0.55)", corner: "#fc26fa" },
  green: { border: "rgba(23,255,14,0.7)", bg: "rgba(0,30,5,0.4)", corner: "#17ff0e" }
}

const CpFrame = styled.div`
  position: relative;
  border: 1px solid ${p => (TONES[p.tone] || TONES.primary).border};
  background: ${p => (TONES[p.tone] || TONES.primary).bg};
  color: ${p => (TONES[p.tone] || TONES.primary).corner};

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 14px;
    height: 14px;
    border-color: currentColor;
    pointer-events: none;
  }
  &::before {
    top: -2px;
    left: -2px;
    border-top: 2px solid;
    border-left: 2px solid;
  }
  &::after {
    top: -2px;
    right: -2px;
    border-top: 2px solid;
    border-right: 2px solid;
  }
`

const CornerBL = styled.div`
  position: absolute;
  bottom: -2px;
  left: -2px;
  width: 14px;
  height: 14px;
  border-bottom: 2px solid;
  border-left: 2px solid;
  border-color: currentColor;
  pointer-events: none;
`

const CornerBR = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  border-bottom: 2px solid;
  border-right: 2px solid;
  border-color: currentColor;
  pointer-events: none;
`

const Corners = () => (
  <React.Fragment>
    <CornerBL />
    <CornerBR />
  </React.Fragment>
)

const FrameHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid
    ${p => (p.tone === "green" ? "rgba(23,255,14,0.3)" : p.tone === "magenta" ? "rgba(252,38,250,0.25)" : "rgba(190,38,252,0.3)")};
  background: linear-gradient(180deg, rgba(190, 38, 252, 0.08), transparent);

  @media (max-width: 600px) {
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px 9px;
    padding: 10px 12px;
  }
`

const HeadTitle = styled.div`
  font-family: "Rajdhani", "VT323", monospace;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${p => (p.tone === "green" ? "#17ff0e" : "#fc26fa")};
  font-size: ${p => (p.big ? "19px" : "15px")};
  ${p => p.big && "text-shadow: 0 0 14px rgba(252,38,250,0.45);"}
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const HeadSub = styled.div`
  font-family: "VT323", monospace;
  color: #6d7da6;
  font-size: 14px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    flex-shrink: 1;
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`

const HeadRight = styled.div`
  margin-left: auto;
  font-family: "VT323", monospace;
  font-size: 14px;
  color: #6d7da6;
  flex-shrink: 0;

  span {
    color: #00e7ff;
  }

  @media (max-width: 600px) {
    margin-left: 0;
  }
`

/* ---------- header bar: breadcrumb replaces the floating buttons ---------- */

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 10px;
  }
`

const Crumb = styled.div`
  font-family: "VT323", monospace;
  font-size: 15px;
  color: #6d7da6;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  text-transform: uppercase;
  flex: 1;

  .sep {
    color: #3a4670;
  }
  .cell {
    color: #fc26fa;
  }
`

const HeadActions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 100%;
    justify-content: flex-end;
  }
`

/* ---------- shared thread grid ---------- */

const ThreadRow = styled.div`
  display: grid;
  grid-template-columns: ${RAIL}px 1fr;
  padding: 16px 20px 16px 0;

  @media (max-width: 600px) {
    grid-template-columns: 88px minmax(0, 1fr);
    padding: 14px 12px 14px 0;
  }
`

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;

  @media (max-width: 600px) {
    img {
      width: 64px !important;
      height: 64px !important;
    }
  }
`

const RailName = styled.div`
  font-family: "VT323", monospace;
  font-size: 15px;
  color: #00e7ff;
  line-height: 1;
  word-break: break-word;
  text-align: center;
`

const RailMsgs = styled.div`
  font-family: "VT323", monospace;
  font-size: 12px;
  color: #ffb800;
  letter-spacing: 0.08em;
`

const PostBody = styled.div`
  min-width: 0;
  display: flex;
  gap: 12px;
  padding-top: 2px;

  @media (max-width: 600px) {
    gap: 9px;
  }
`

const AccentBar = styled.div`
  width: 2px;
  align-self: stretch;
  background: rgba(252, 38, 250, 0.5);
  flex-shrink: 0;
`

const BodyText = styled.div`
  font-size: 16.5px;
  color: #d6e1ff;
  line-height: 1.55;
  word-break: break-word;
`

const PostFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px 10px ${RAIL}px;
  border-top: 1px solid rgba(252, 38, 250, 0.2);
  font-family: "VT323", monospace;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    padding: 10px 12px;
    gap: 8px;
  }
`

const ReplyCount = styled.span`
  font-size: 14px;
  color: #00e7ff;
`

const CellPill = styled(Link)`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  padding: 3px 9px;
  border: 1px solid currentColor;
  font-family: "VT323", monospace;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #fc26fa;
  background: rgba(252, 38, 250, 0.06);
  text-decoration: none;
  overflow-wrap: anywhere;
  word-break: break-word;

  &:hover {
    color: #00e7ff;
  }
`

const FootId = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #3a4670;

  @media (max-width: 480px) {
    width: 100%;
    margin-left: 0;
  }
`

/* ---------- replies ---------- */

const SpineWrap = styled.div`
  position: relative;
`

const Spine = styled.div`
  position: absolute;
  left: ${SPINE_X}px;
  top: 10px;
  bottom: 10px;
  width: 1px;
  background: rgba(190, 38, 252, 0.25);

  @media (max-width: 600px) {
    left: 44px;
  }
`

const EmptyReplies = styled.div`
  padding: 34px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;

  .glyph {
    font-size: 34px;
    color: #3a4670;
    line-height: 1;
  }
  .head {
    font-family: "Rajdhani", "VT323", monospace;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: #6d7da6;
  }
  .sub {
    font-family: "VT323", monospace;
    font-size: 14px;
    color: #3a4670;
  }
`

/* ---------- composer ---------- */

const ComposerBody = styled.div`
  display: grid;
  grid-template-columns: ${RAIL}px 1fr;
  padding: 14px 20px 16px 0;

  @media (max-width: 600px) {
    grid-template-columns: 60px minmax(0, 1fr);
    padding: 12px 12px 14px 0;
  }
`

const ComposerRailCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const ComposerMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  input {
    width: 100%;
    background: rgba(0, 231, 255, 0.04);
    border: 1px solid rgba(0, 231, 255, 0.25);
    border-bottom: 1px solid #00e7ff;
    color: #00e7ff;
    font-family: "VT323", monospace;
    font-size: 16px;
    padding: 10px 12px;
    outline: none;
    box-sizing: border-box;
  }

  input:focus {
    border-color: #fc26fa;
    color: #fc26fa;
  }

  input::placeholder {
    color: rgba(0, 231, 255, 0.4);
  }
`

const ComposerActions = styled.div`
  display: flex;
  align-items: center;
`

class PostInfo extends Component {
  state = {
    showText: false
  }
  componentDidMount() {
    const { match } = this.props
    this.props.fetchSinglePost(match.params.postId)
    this.focusReplyFromHash()
    setTimeout(() => {
      this.setState({
        showText: true
      })
    }, 700)
  }

  componentDidUpdate(prevProps) {
    // Same component instance serves every /post/:postId — refetch when
    // the id changes or the previous post's state (or error) sticks.
    const prevId = prevProps.match.params.postId
    const newId = this.props.match.params.postId
    if (prevId !== newId) {
      this.props.fetchSinglePost(newId)
    }

    const openedReply =
      this.props.location.hash === "#reply" &&
      (prevProps.location.hash !== "#reply" ||
        prevProps.isLoading !== this.props.isLoading ||
        prevId !== newId)
    if (openedReply) {
      this.focusReplyFromHash()
    }
  }

  focusReplyFromHash = () => {
    if (this.props.location.hash !== "#reply") return
    setTimeout(() => {
      const composer = document.getElementById("reply")
      if (!composer) return
      composer.scrollIntoView({ behavior: "smooth", block: "start" })
      const input = composer.querySelector("input")
      if (input) input.focus()
    }, 0)
  }

  cellOf() {
    const { post } = this.props
    const raw = post && post.cellId
    if (!raw) return null
    if (raw._id) return { _id: raw._id, name: raw.name }
    return { _id: raw, name: null }
  }

  deletePost = async postId => {
    const cell = this.cellOf()
    const cellId = cell && cell._id
    // Wait for the DELETE + scoped re-fetch to finish before navigating.
    // Without this, the user lands on the cell page before the server
    // has actually removed the post — the new page mounts and re-fetches
    // a list that still contains the just-deleted entry.
    const ok = await this.props.deletePost(postId, cellId)
    if (ok === false) {
      return
    }
    this.props.history.push(cellId ? `/cell/${cellId}` : "/cells")
  }

  goBack = () => {
    const cell = this.cellOf()
    this.props.history.push(cell ? `/cell/${cell._id}` : "/grid")
  }

  submit = comment => {
    if (!comment || !comment.text || !comment.text.trim()) {
      return
    }
    const { match, user } = this.props
    const getPostId = match.params.postId
    const getUserId = get(user, "loaded.user._id")
    this.props.commentPost(getUserId, getPostId, comment)
    this.props.dispatch(reset("PostInfo"))
  }

  renderHeaderBar(canModify) {
    const { post } = this.props
    const cell = this.cellOf()
    const tx = post._id ? `TX-${String(post._id).slice(-4).toUpperCase()}` : ""
    return (
      <HeaderBar>
        <Button animate layer="control" onClick={this.goBack}>
          ◂ BACK
        </Button>
        <Crumb>
          THE GRID <span className="sep">{"//"}</span>{" "}
          {cell && cell.name && (
            <React.Fragment>
              <span className="cell">#{cell.name}</span>{" "}
              <span className="sep">{"//"}</span>{" "}
            </React.Fragment>
          )}
          {tx}
        </Crumb>
        {canModify && (
          <HeadActions>
            <Button
              animate
              layer="success"
              onClick={() => this.props.history.push(`/editPost/${post._id}`)}
            >
              <Icon path={mdiChemicalWeapon} size={0.5} color="green" spin />{" "}
              EDIT
            </Button>
            <Button
              animate
              layer="alert"
              onClick={() => this.deletePost(post._id)}
            >
              <Icon path={mdiRobot} size={0.5} color="red" /> DELETE
            </Button>
          </HeadActions>
        )}
      </HeaderBar>
    )
  }

  renderPost() {
    const { post } = this.props
    const cell = this.cellOf()
    const comments = get(post, "comments", [])
    const tx = post._id ? `TX-${String(post._id).slice(-4).toUpperCase()}` : ""
    const shortId = post._id
      ? `${String(post._id).slice(0, 4)}…${String(post._id).slice(-4)}`
      : ""

    return (
      <CpFrame tone="magenta">
        <Corners />
        <FrameHead tone="magenta">
          <HeadTitle big>{post.title || "loading"}</HeadTitle>
          <HeadSub>{tx}</HeadSub>
          <HeadRight>
            POSTED <span>{formatPostDate(post.created).toUpperCase()}</span>
          </HeadRight>
        </FrameHead>

        <ThreadRow>
          <Rail>
            <UserAvatar user={post.postedBy} size={72} square />
            <RailName>
              {post.postedBy ? post.postedBy.name : "deletedUser"}
            </RailName>
            {post.postedBy && post.postedBy.messageCount != null && (
              <RailMsgs>MSGS: {post.postedBy.messageCount}</RailMsgs>
            )}
          </Rail>
          <PostBody>
            <AccentBar />
            <BodyText>
              <Words animate show={this.state.showText}>
                {post.body || ""}
              </Words>
            </BodyText>
          </PostBody>
        </ThreadRow>

        <PostFooter>
          <ReplyCount>◌ {comments.length} REPLIES</ReplyCount>
          {cell && cell.name && (
            <CellPill to={`/cell/${cell._id}`}>#{cell.name}</CellPill>
          )}
          <FootId>ID {shortId}</FootId>
        </PostFooter>
      </CpFrame>
    )
  }

  renderReplies() {
    const { match, user, post, isAdmin } = this.props
    const getPostId = match.params.postId
    const getUserId = get(user, "loaded.user._id")
    const comments = get(post, "comments", [])
    const opId = get(post, "postedBy._id")

    return (
      <CpFrame>
        <Corners />
        <FrameHead>
          <HeadTitle>◢ INCOMING REPLIES</HeadTitle>
          <HeadSub>· {comments.length}</HeadSub>
        </FrameHead>
        {comments.length === 0 ? (
          <EmptyReplies>
            <div className="glyph">◌</div>
            <div className="head">NO REPLIES YET</div>
            <div className="sub">
              {"//"} this transmission is unanswered — be the first to respond
            </div>
          </EmptyReplies>
        ) : (
          <SpineWrap>
            <Spine />
            <div style={{ position: "relative" }}>
              {comments.map((comment, i) => (
                <Comment
                  key={comment._id || i}
                  comment={comment}
                  index={i}
                  isOp={
                    !!opId &&
                    String(get(comment, "postedBy._id")) === String(opId)
                  }
                  last={i === comments.length - 1}
                  postId={getPostId}
                  userId={getUserId}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          </SpineWrap>
        )}
      </CpFrame>
    )
  }

  renderComposer() {
    const { user, formValues } = this.props
    const isLoggedIn = !!get(user, "loaded.token")
    if (!isLoggedIn) {
      return null
    }
    const viewer = get(user, "loaded.user") || {}
    const text = get(formValues, "text", "")
    return (
      <CpFrame tone="green">
        <Corners />
        <FrameHead tone="green">
          <HeadTitle tone="green">◢ TRANSMIT A REPLY</HeadTitle>
          <HeadSub>{"//"} replying as {viewer.name || "operator"}</HeadSub>
        </FrameHead>
        <ComposerBody>
          <ComposerRailCell>
            <UserAvatar user={viewer} size={44} square />
          </ComposerRailCell>
          <ComposerMain>
            <Field
              name="text"
              component="input"
              placeholder="type your reply, operator…"
              autoComplete="off"
            />
            <ComposerActions>
              <Button
                animate
                layer="success"
                disabled={!text.trim()}
                onClick={this.props.handleSubmit(this.submit)}
                style={{ marginLeft: "auto" }}
              >
                <Icon path={mdiChemicalWeapon} size={0.5} color="#17ff0e" spin />{" "}
                TRANSMIT ▸
              </Button>
            </ComposerActions>
          </ComposerMain>
        </ComposerBody>
      </CpFrame>
    )
  }

  render() {
    const { post, isLoading, isError, user, isAdmin, isExpanded } = this.props

    if (isLoading) {
      return (
        <Arwes>
          <PageOffset isExpanded={isExpanded}>
            <div
              style={{ position: "relative", height: 300, margin: "0 auto" }}
            >
              <Loading animate full />
            </div>
          </PageOffset>
        </Arwes>
      )
    }

    if (isError) {
      return (
        <Arwes>
          <PageOffset isExpanded={isExpanded}>
            <MarginTop />
            <ContentColumn maxWidth={940}>
              <PageBlock
                style={{ textAlign: "center", fontFamily: "VT323, monospace" }}
              >
                <div style={{ color: "#ff001c", marginBottom: 20 }}>
                  TRANSMISSION LOST // this post could not be loaded
                </div>
                <Button
                  animate
                  layer="alert"
                  onClick={() => this.props.history.push("/grid")}
                >
                  BACK TO THE GRID
                </Button>
              </PageBlock>
            </ContentColumn>
          </PageOffset>
        </Arwes>
      )
    }

    // Mirror the backend isPoster rule: only the author or an admin may
    // edit/delete, so nobody else gets buttons that would just 403.
    const viewerId = get(user, "loaded.user._id")
    const authorId = post && post.postedBy && post.postedBy._id
    const canModify =
      !!viewerId && (isAdmin || (authorId && String(authorId) === String(viewerId)))

    return (
      <Arwes>
        <PageOffset isExpanded={isExpanded}>
          <MarginTop />
          <ContentColumn maxWidth={940}>
            <PageBlock>{this.renderHeaderBar(canModify)}</PageBlock>
            <PageBlock>{this.renderPost()}</PageBlock>
            <PageBlock>{this.renderReplies()}</PageBlock>
            <PageBlock id="reply">{this.renderComposer()}</PageBlock>
          </ContentColumn>
        </PageOffset>
      </Arwes>
    )
  }
}

const FORM_NAME = "PostInfo"
const form = reduxForm({ form: FORM_NAME })(PostInfo)

const mapStateToProps = state => {
  return {
    formValues: getFormValues(FORM_NAME)(state),
    post: state.posts.single,
    isLoading: state.posts.isLoading,
    isError: state.posts.isError,
    user: state.user,
    isAdmin: isAdminUser(state)
  }
}

export default connect(
  mapStateToProps,
  { fetchSinglePost, deletePost, commentPost }
)(form)
